import { useMemo, useRef } from "react";
import {
  Column,
  MuiTable,
  MuiTypography,
  RowProps,
} from "../../../../../../../../components";
import { useSiteCore } from "../../../../../../../../hooks/useSiteCore";
import {
  ADD_PROJECT_FEE_AND_EXPENSE_TYPES,
  ProjectAddExpenseOrFeeSummeryColumns,
  ProjectAddOrEditExpenseOrFeeFormKey,
} from "../../../../../../modelsAndSchemas/Models";
import { Box } from "@mui/material";
import { projectAddFeesAndExpensesSiteCoreKeys } from "../../../../../../siteCore/SiteCoreKeys";
import { useFormContext } from "react-hook-form";
import {
  calculateTotalTaxAmount,
  getFormattedAmount,
} from "../../../../../../../../helpers/utils";
import { CURRENCY_FORMAT_MAP } from "../../../../../../../../components/textFieldCurrency/CurrencyUtils";

interface ProjectAddFeesAndExpenseSummaryProps {
  type: string;
}

export const ProjectAddFeesAndExpenseSummary = ({
  type,
}: ProjectAddFeesAndExpenseSummaryProps) => {
  const { commonLabels, useFetchLabel } = useSiteCore();
  const getExpenseSummaryLabel = useFetchLabel(
    projectAddFeesAndExpensesSiteCoreKeys.addFeesAndExpenses,
  );
  const expenseSummaryTableRef = useRef<{ resetPagination: () => void }>(null);
  const { getValues } = useFormContext();

  const expenseTableData = useMemo(() => {
    const {
      feeOrExpenseName,
      feeOrExpenseAmountWithFormat,
      billingAmount,
      feeOrExpenseCurrencyName,
      billingCurrencyName,
      billingCurrencyExchangeRate,
      billingAmountWithFormat,
      taxEntries,
    } = getValues();
    const billingCurrencyFormat = CURRENCY_FORMAT_MAP[billingCurrencyName];
    const projectTotalTax = taxEntries?.length
      ? calculateTotalTaxAmount(taxEntries)
      : 0;
    const projectTotalTaxWithFormat = getFormattedAmount(
      projectTotalTax,
      billingCurrencyFormat?.locale,
    );
    const totalProjectInvoiceAmount = Number(billingAmount) + projectTotalTax;
    const totalProjectInvoiceAmountWithFormat = getFormattedAmount(
      totalProjectInvoiceAmount,
      billingCurrencyFormat?.locale,
    );
    return [
      {
        feeOrExpenseName,
        [ProjectAddOrEditExpenseOrFeeFormKey.projectFeeOrExpenseAmountWithFormat]: `${feeOrExpenseCurrencyName} ${feeOrExpenseAmountWithFormat}`,
        billingCurrencyExchangeRate,
        [ProjectAddOrEditExpenseOrFeeFormKey.projectBillingAmountWithFormat]: `${billingCurrencyName} ${billingAmountWithFormat}`,
        [ProjectAddOrEditExpenseOrFeeFormKey.projectTax]: `${billingCurrencyName} ${projectTotalTaxWithFormat}`,
        totalAmount: `${billingCurrencyName} ${totalProjectInvoiceAmountWithFormat}`,
      },
    ];
  }, []);

  const columns: Column[] = [
    {
      id: ProjectAddOrEditExpenseOrFeeFormKey.projectFeeOrExpenseName,
      label: getExpenseSummaryLabel(
        type === ADD_PROJECT_FEE_AND_EXPENSE_TYPES.PROJECT_OR_PROJECT_FEE
          ? projectAddFeesAndExpensesSiteCoreKeys.summaryTabFeeName
          : projectAddFeesAndExpensesSiteCoreKeys.expenseName,
      ),
    },
    {
      id: ProjectAddOrEditExpenseOrFeeFormKey.projectFeeOrExpenseAmountWithFormat,
      label: getExpenseSummaryLabel(
        type === ADD_PROJECT_FEE_AND_EXPENSE_TYPES.PROJECT_OR_PROJECT_FEE
          ? projectAddFeesAndExpensesSiteCoreKeys.summaryTabFeeAmount
          : projectAddFeesAndExpensesSiteCoreKeys.expenseDetailsAmount,
      ),
    },
    {
      id: ProjectAddOrEditExpenseOrFeeFormKey.projectBillingCurrencyExchangeRate,
      label: getExpenseSummaryLabel(projectAddFeesAndExpensesSiteCoreKeys.summaryTabExchangeRate),
    },
    {
      id: ProjectAddOrEditExpenseOrFeeFormKey.projectBillingAmountWithFormat,
      label: getExpenseSummaryLabel(projectAddFeesAndExpensesSiteCoreKeys.summaryTabBillingAmount),
    },
    {
      id: ProjectAddOrEditExpenseOrFeeFormKey.projectTax,
      label: getExpenseSummaryLabel(projectAddFeesAndExpensesSiteCoreKeys.summaryTabTax),
    },
    {
      id: ProjectAddExpenseOrFeeSummeryColumns.projectTotalAmount,
      label: getExpenseSummaryLabel(
        projectAddFeesAndExpensesSiteCoreKeys.summaryTabTotalInvoiceAmount,
      ),
      className: "text-right",
      render: (row: RowProps) => (
        <Box className="text-right">
          <MuiTypography variant="h3Bold">
            {row?.[ProjectAddExpenseOrFeeSummeryColumns.projectTotalAmount]}
          </MuiTypography>
        </Box>
      ),
    },
  ];

  return (
    <Box className="summary-table-container">
      <MuiTable
        ref={expenseSummaryTableRef}
        pageId={"addFeesAndExpenseSummary"}
        tableHeaderForm={[]}
        columns={columns}
        tableResults={expenseTableData}
        emptyMessage={commonLabels.noRecordsFound}
        noResultMessage={commonLabels.searchCriteria}
        priorityResults={[]}
        isShowDashIfValueEmpty
        noDataInApi={false}
        isFooterVisible={false}
      />
    </Box>
  );
};
